import { Chip, type ChipProps } from '@mui/material';
import { designTokens } from '../tokens';

export type ValueBadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'teal';

export type ValueBadgeProps = Pick<ChipProps, 'label' | 'icon' | 'id' | 'title' | 'aria-label' | 'aria-describedby' | 'role' | 'aria-live' | 'aria-atomic'> & {
  tone?: ValueBadgeTone;
  size?: 'small' | 'compact';
  maxWidth?: number | string;
};

const colors = {
  neutral: 'default',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  accent: 'secondary',
  teal: 'success',
} as const;

const toneStyles = {
  neutral: { color: designTokens.color.neutralText, backgroundColor: designTokens.color.subtleSurface },
  info: { color: designTokens.color.infoDark, backgroundColor: designTokens.color.infoSoft },
  success: { color: designTokens.color.successText, backgroundColor: designTokens.color.successSoft },
  warning: { color: designTokens.color.warningDark, backgroundColor: designTokens.color.warningSoft },
  danger: { color: designTokens.color.dangerText, backgroundColor: designTokens.color.dangerSoft },
  accent: { color: designTokens.color.accentText, backgroundColor: designTokens.color.accentSoft },
  teal: { color: designTokens.color.inverseSuccessSurface, backgroundColor: designTokens.color.inverseSuccessText },
};

/** 상태·유형 등 확정된 값을 표시하며 선택·삭제 동작은 소유하지 않습니다. */
export function ValueBadge({ tone = 'neutral', size = 'small', maxWidth = '100%', title, label, ...props }: ValueBadgeProps) {
  return <Chip {...props} component="span" variant="filled" size="small" color={colors[tone]} label={label}
    title={title ?? (typeof label === 'string' ? label : undefined)}
    sx={{
      ...toneStyles[tone],
      ...designTokens.typography.supporting,
      height: size === 'compact' ? designTokens.layout.valueBadgeCompactHeight : designTokens.layout.valueBadgeHeight,
      border: 0,
      borderRadius: size === 'compact' ? designTokens.layout.valueBadgeCompactRadius : designTokens.layout.valueBadgeRadius,
      fontWeight: designTokens.layout.valueBadgeFontWeight,
      px: size === 'compact' ? 0.75 : 1,
      gap: `${designTokens.layout.valueBadgeGap}px`,
      flexShrink: 0,
      maxWidth,
      verticalAlign: 'middle',
      '& .MuiChip-label': { p: 0 },
      '& .MuiChip-icon': { m: 0, color: 'inherit', fontSize: designTokens.layout.valueBadgeIconSize },
      '@media (forced-colors: active)': { border: '1px solid CanvasText' },
    }} />;
}
